import { BookingHistoryCard } from "./BookingHistoryCard";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMobile } from "@/hooks/use-mobile";
import DesktopBookingHistoryList from "@/features/booking-history/DesktopBookingHistoryList";
import type { BookingHistoryData } from "@/types/booking-history/bookinghistory";
import { BookingHistorySkeleton } from "@/pages/booking-history/BookingHistorySkeleton";

const PAGER_BTN = "rounded-2xl bg-card size-14 border-border";

type BookingHistoryListProps = {
  loading: boolean;
  bookingHistory: BookingHistoryData | null;
  onPageChange: (page: number) => void;
};

export function BookingHistoryList({
  loading,
  bookingHistory,
  onPageChange,
}: BookingHistoryListProps) {
  const isMobile = useMobile();
  const visibleCount = isMobile ? 2 : 4;

  // Loading
  if (loading) {
    return <BookingHistorySkeleton isMobile={isMobile} />;
  }

  const page = Number(bookingHistory?.pageNo ?? 1);
  const pageSize = Number(bookingHistory?.pageSize ?? 5);
  const totalCount = Number(bookingHistory?.totalCount ?? 0);
  const items = bookingHistory?.items ?? [];

  const totalPages = Math.ceil(totalCount / pageSize);

  function getPageItems(
    current: number,
    total: number,
    visibleCount: number,
  ): (number | "ellipsis")[] {
    const range = (start: number, end: number) =>
      Array.from({ length: end - start + 1 }, (_, index) => start + index);

    if (total <= visibleCount) {
      return range(1, total);
    }

    const windowStart = Math.max(1, current - 1);
    const windowEnd = Math.min(windowStart + visibleCount - 1, total);

    const items: (number | "ellipsis")[] = range(windowStart, windowEnd);

    if (windowEnd < total) {
      items.push("ellipsis");
    }

    return items;
  }

  const goTo = (nextPage: number) => {
    const newPage = Math.min(Math.max(1, nextPage), totalPages);

    if (newPage !== page) {
      onPageChange(newPage);
    }
  };

  // Calculate "Showing 1-5 of 20"
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);

  return (
    <>
      {/* Booking list */}
      {isMobile ? (
        <div className="mt-2 flex flex-col gap-2">
          {items.length === 0 ? (
            <div className="group rounded-2xl bg-card p-4 shadow-xl transition-shadow hover:shadow-2xl">
              <p className="text-sm text-muted-foreground">No booking found</p>
            </div>
          ) : (
            items.map((booking) => (
              <BookingHistoryCard key={booking.paymentId} booking={booking} />
            ))
          )}
        </div>
      ) : (
        <div className="mt-2">
          {/* Header */}
          <div className="mt-2 grid grid-cols-10 items-center gap-4 rounded-t-[16px] bg-primary px-6 py-2">
            <div className="text-sm font-medium text-primary-foreground">
              Booking no.
            </div>

            <div className="col-span-3 text-sm font-medium text-primary-foreground">
              Route
            </div>

            <div className="col-span-2 text-sm font-medium text-primary-foreground">
              Departure
            </div>

            <div className="col-span-2 text-sm font-medium text-primary-foreground">
              Return
            </div>

            <div className="text-sm font-medium text-primary-foreground">
              Pax.
            </div>

            <div className="text-center text-sm font-medium text-primary-foreground">
              Action
            </div>
          </div>

          {/* Items */}
          <div className="flex flex-col">
            <div className="overflow-hidden rounded-b-2xl bg-card px-6 shadow-xl">
              {items.length === 0 ? (
                <div className="flex items-center justify-center py-2">
                  <p className="text-sm font-medium text-muted-foreground">
                    No booking found
                  </p>
                </div>
              ) : (
                items.map((booking, index) => (
                  <div
                    key={booking.paymentId}
                    className={index > 0 ? "border-t border-border" : ""}
                  >
                    <DesktopBookingHistoryList booking={booking} />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {items.length > 0 && (
        <Pagination className="mt-6">
          <PaginationContent className="w-full gap-4">
            {/* Previous */}
            <PaginationItem>
              <PaginationPrevious
                text=""
                onClick={() => goTo(page - 1)}
                aria-disabled={page === 1}
                className={cn(
                  PAGER_BTN,
                  page === 1
                    ? "pointer-events-none opacity-50"
                    : "hover:bg-primary-soft",
                )}
              />
            </PaginationItem>

            {/* Page numbers */}
            <li className="flex flex-1 items-center justify-center gap-4">
              {getPageItems(page, totalPages, visibleCount).map((item, index) =>
                item === "ellipsis" ? (
                  <PaginationEllipsis
                    key={`ellipsis-${index}`}
                    className={cn("border", PAGER_BTN)}
                  />
                ) : (
                  <PaginationLink
                    key={item}
                    isActive={item === page}
                    onClick={() => goTo(item)}
                    className={cn(
                      PAGER_BTN,
                      "text-sm font-bold",
                      item === page
                        ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                        : "bg-card text-secondary-foreground hover:bg-primary-soft",
                    )}
                  >
                    {item}
                  </PaginationLink>
                ),
              )}
            </li>

            {/* Next */}
            <PaginationItem>
              <PaginationNext
                text=""
                onClick={() => goTo(page + 1)}
                aria-disabled={page === totalPages}
                className={cn(
                  PAGER_BTN,
                  page === totalPages
                    ? "pointer-events-none opacity-50"
                    : "hover:bg-primary-soft",
                )}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {/* Showing */}
      {items.length > 0 && (
        <p className="mt-2.5 text-center text-xs font-medium text-field">
          Showing {start}-{end} of {totalCount}
        </p>
      )}
    </>
  );
}

function PaginationPrevious({
  className,
  text = "Previous",
  ...props
}: React.ComponentProps<typeof PaginationLink> & {
  text?: string;
}) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      className={cn(className)}
      {...props}
    >
      <ChevronLeft className="size-5" />
      {text && <span className="hidden sm:block">{text}</span>}
    </PaginationLink>
  );
}

function PaginationNext({
  className,
  text = "Next",
  ...props
}: React.ComponentProps<typeof PaginationLink> & {
  text?: string;
}) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      className={cn(className)}
      {...props}
    >
      {text && <span className="hidden sm:block">{text}</span>}
      <ChevronRight className="size-5" />
    </PaginationLink>
  );
}
